import { Col, Row } from "antd";
import { useFormik } from "formik";
import React, { CSSProperties, useEffect } from "react";
import styled from "styled-components";

import moment from "moment";
import { ReactComponent as ArrowDown } from "../../../assets/icons/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "../../../assets/icons/ArrowUp.svg";
import { ReactComponent as Reset } from "../../../assets/icons/Reset.svg";
import { theme } from "../../../theme";
import Button from "../../atoms/Button";
import DatePicker from "../../atoms/DatePicker";
import DateRangePicker from "../../atoms/DateRangePicker";
import Input from "../../atoms/Input";
import RadioGroup from "../../atoms/RadioGroup";
import Select from "../../atoms/Select";
import { ArrowDownIcon } from "../../organisms/Icon";
import { TableFilterFormElementType } from "./enums";
import { TableFilterForm } from "./interfaces";
import {
  FilterLabel,
  FilterTitle,
  IconWrapper,
  SearchActionContainer,
  SearchFormElementContainer,
  TableFilterContainer,
} from "./styles";

interface TableFilterProps {
  filterForms: TableFilterForm[];
  style?: CSSProperties;
  onSearchSubmit?: (filterValues: any) => void;
  onSearchReset?: () => void;
  filterValues?: Record<string, any>;
  initialFilterValues?: Record<string, any>;
  isUnFold: boolean;
  setIsUnFold: (e: boolean) => void;
}

const TableFilter: React.FC<TableFilterProps> = (props: TableFilterProps) => {
  const {
    style,
    initialFilterValues = {},
    filterValues = {},
    filterForms,
    onSearchSubmit,
    onSearchReset,
    isUnFold,
    setIsUnFold,
  } = props;

  const formik = useFormik({
    initialValues: initialFilterValues,
    onSubmit: (values) => {
      if (onSearchSubmit) onSearchSubmit(values);
    },
    onReset: () => {
      if (onSearchReset) onSearchReset();
    },
  });

  useEffect(() => {
    formik.setValues(filterValues);
  }, [filterValues]);

  const colProps = {
    span: 8,
    xs: 24,
    sm: 8,
    md: 8,
    lg: 8,
    xl: 8,
    xxl: 8,
  };

  const formElementStyle = {
    width: "100%",
  };

  const renderFormElement = (form: TableFilterForm) => {
    const { type, key, options, placeholder, render, dateRangeKeys } = form;
    const { values, setFieldValue } = formik;
    const value = values[key];
    switch (type) {
      case TableFilterFormElementType.Input:
        return (
          <Input
            style={formElementStyle}
            value={value}
            placeholder={placeholder}
            allowClear
            showCount={false}
            onPressEnter={() => formik.handleSubmit()}
            onChange={({ target }) => {
              const { value } = target;
              setFieldValue(key, value);
            }}
          />
        );
      case TableFilterFormElementType.NumberInput:
        break;
      case TableFilterFormElementType.Select:
        return (
          <Select
            style={formElementStyle}
            options={options}
            value={value}
            allowClear
            placeholder={placeholder}
            suffixIcon={<ArrowDownIcon width={16} height={16} />}
            onChange={(value) => {
              setFieldValue(key, value);
              formik.handleSubmit();
            }}
          />
        );
      case TableFilterFormElementType.Radio:
        return (
          <RadioGroup
            style={formElementStyle}
            value={value}
            options={options}
            onChange={({ target }) => {
              const { value } = target;
              setFieldValue(key, value);
              formik.handleSubmit();
            }}
          />
        );
      case TableFilterFormElementType.DateRangePicker: {
        if (dateRangeKeys) {
          const key1 = dateRangeKeys[0];
          const key2 = dateRangeKeys[1];
          return (
            <DateRangePicker
              style={formElementStyle}
              value={
                values[key1]
                  ? [moment(values[key1]), moment(values[key2])]
                  : undefined
              }
              onChange={(dates) => {
                setFieldValue(key, dates);
                if (dates) {
                  setFieldValue(
                    key1,
                    dates[0]
                      ?.hours(9)
                      .milliseconds(0)
                      .minutes(0)
                      .seconds(0)
                      .toISOString()
                  );
                  setFieldValue(
                    key2,
                    dates[1]
                      ?.hours(9)
                      .milliseconds(0)
                      .minutes(0)
                      .seconds(0)
                      .toISOString()
                  );
                } else {
                  setFieldValue(key1, "");
                  setFieldValue(key2, "");
                }
                formik.handleSubmit();
              }}
            />
          );
        }
        return (
          <DateRangePicker
            style={formElementStyle}
            value={value}
            onChange={(value, timeString) => {}}
          />
        );
      }
      case TableFilterFormElementType.RadioDateRangePicker:
      case TableFilterFormElementType.DatePicker:
        return (
          <DatePicker
            style={formElementStyle}
            value={value ? moment(value) : undefined}
            onChange={(e) => {
              setFieldValue(
                key,
                e?.hours(9).milliseconds(0).minutes(0).seconds(0).toISOString()
              );
            }}
          />
        );
      case TableFilterFormElementType.NumberRange:
        break;
      case TableFilterFormElementType.Custom:
        if (render) return render(formik);
        break;
      default:
        return "";
    }
    return "";
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <SearchActionContainer isFold={!isUnFold}>
        <FilterTitle>필터</FilterTitle>
        <Row wrap={false}>
          <Col>
            <ResetButton onClick={() => formik.resetForm()}>
              <Reset fill="black" />
            </ResetButton>
          </Col>
          <Col>
            <IconWrapper
              onClick={() => {
                setIsUnFold(!isUnFold);
              }}
            >
              {!isUnFold ? <ArrowDown /> : <ArrowUp />}
            </IconWrapper>
          </Col>
          <Col>
            <Button htmlType="submit">검색</Button>
          </Col>
        </Row>
      </SearchActionContainer>
      <TableFilterContainer isFold={!isUnFold} style={style}>
        <SearchFormElementContainer>
          <Row gutter={[24, 16]} align="middle">
            {filterForms.map((form) => {
              const { label, key } = form;
              return (
                <Col key={key} {...colProps}>
                  <Row justify="space-between" align="middle" wrap={false}>
                    <Col flex="0 0 100px">
                      <FilterLabel>{label}</FilterLabel>
                    </Col>
                    <StyledCol>{renderFormElement(form)}</StyledCol>
                  </Row>
                </Col>
              );
            })}
          </Row>
        </SearchFormElementContainer>
      </TableFilterContainer>
      {/* <SearchActionContainer>
        <ResetButton onClick={() => formik.resetForm()}>
          <Reset />
        </ResetButton>
        <Button htmlType="submit">검색하기</Button>
      </SearchActionContainer> */}
    </form>
  );
};

export default TableFilter;

const ResetButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  width: 32px;
  height: 32px;
  border: 1px solid ${theme.colors.gray6};
  margin-right: 12px;
  cursor: pointer;
`;
const StyledCol = styled(Col)`
  -webkit-box-flex: 1;
  -ms-flex: 1;
  flex-grow: 1;
`;
